<template>
  <div :class="props.class">
    <div class="col-md-12 px-0 mb-5">
      <div class="d-flex align-items-center">
        <label class="form-label size-12 lh-12">{{ props.title }}</label>
        <img
          src="@/assets/images/icons/question.svg"
          alt="image"
          class="ms-2 mb-2"
          data-bs-toggle="tooltip"
          data-bs-placement="bottom"
          data-bs-custom-class="tooltip-fourth"
          data-bs-title="※アップロードいただいた情報は、今後の選考時に参考にさせていただく可能性がございます。"
        />
      </div>
      <div
        class="upload-pdf__file__form bg-silver-primary d-flex flex-column align-items-center pt-4 pb-2r rounded-2"
        @drop.prevent="handleDrop"
        @dragenter="entering = true"
        @dragleave="entering = false"
      >
        <img src="@/assets/image/icon-upload.svg" class="w-40x h-40x" role="button" />
        <p class="color-grey-secondary size-12 lh-12 mb-0 mt-12x">
          PDF, docx, xlsxファイルをここにドラッグ＆ドロップ （{{ maxSizeInMB }}MB以内）
        </p>
        <div class="d-flex justify-content-between align-items-center">
          <p v-if="fileName" class="mb-0 mt-2">{{ fileName }}</p>
          <div>
            <img
              v-if="fileName"
              src="@/assets/images/icons/trash.svg"
              class="ms-3"
              role="button"
              @click="removeFile(props.fileType)"
            />
          </div>
        </div>
        <p v-if="fileName && fieldError" class="mb-0 size-12 text-danger">{{ fieldError }}</p>
        <input
          :id="`file-pdf-${id}`"
          :accept="accept"
          type="file"
          :name="`file-pdf-${id}`"
          hidden
          @change="handleInput($event)"
        />
        <label
          :for="`file-pdf-${id}`"
          class="btn btn-outline-secondary d-flex justify-content-center size-12 w-12 rounded-5 mt-4 h-36x align-items-center"
        >
          ファイルを選択
        </label>
        <div v-if="messageError" class="d-block d-flex justify-content-center invalid-feedback">
          {{ messageError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, computed, ref, watch } from 'vue';
import { useOptionalInformationStore } from '@/stores/resume-candidate/OptionalInformation.store';
import { useMessageStore } from '@/stores/messages.store';
import * as bootstrap from 'bootstrap';
import { MAX_UPLOAD_FILE_SIZE_MB } from '@/utils/constants';

const messageError = ref('');
const events = ['dragenter', 'dragleave', 'dragover', 'drop'];
const contentTypes = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const entering = ref(false);
const fileName = ref('');
const emit = defineEmits<{ (e: 'upload', file: File): void }>();
const useOptionalInformation = useOptionalInformationStore();
const messageStore = useMessageStore();
const maxSizeInMB = MAX_UPLOAD_FILE_SIZE_MB;
const maxSizeInBytes = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

watch(
  () => props.fileName,
  (newFileName) => {
    fileName.value = newFileName;
  },
  { immediate: true }
);

onMounted(() => {
  events.forEach((event) => document.body.addEventListener(event, (e) => e.preventDefault()));
  new bootstrap.Tooltip(document.body, {
    selector: "[data-bs-toggle='tooltip']",
    customClass: 'tooltip-fourth',
  });
});

onUnmounted(() => {
  events.forEach((event) => document.body.removeEventListener(event, (e) => e.preventDefault()));
});

const fieldError = computed(() => {
  const error = messageStore.field_error.find((error) => error.name === props.fileType);
  return error ? error.message : '';
});

const handleDrop = (e: DragEvent): void => {
  const files = e.dataTransfer?.files as FileList;

  if (!files || !files[0]) return;

  const file = files[0];

  if (file.size > maxSizeInBytes) {
    messageError.value = `${maxSizeInMB}MB以内のファイルをご利用ください。`;
    return;
  }

  if (contentTypes.includes(file.type)) {
    messageError.value = '';
    fileName.value = truncatedFileName(file.name);
    if (!files || !files[0]) return;
    const fileImage = file;
    const reader = new FileReader();

    reader.onload = async (): Promise<void> => {
      emit('upload', fileImage);
    };
    reader.readAsDataURL(fileImage);
  } else {
    messageError.value = 'PDFまたはxlsx, docxの形式をご利用ください。';
  }
};

const handleInput = (e: InputEvent): void => {
  clearFileError();
  const files = (e.target as HTMLInputElement).files as FileList;

  if (!files || !files[0]) return;

  const file = files[0];

  if (file.size > maxSizeInBytes) {
    messageError.value = `${maxSizeInMB}MB以内のファイルをご利用ください。`;
    return;
  }

  if (contentTypes.includes(file.type)) {
    messageError.value = '';
    fileName.value = truncatedFileName(file.name);
    if (!files || !files[0]) return;
    const fileImage = file;
    const reader = new FileReader();

    reader.onload = async (): Promise<void> => {
      emit('upload', fileImage);
    };
    reader.readAsDataURL(fileImage);
  } else {
    messageError.value = 'PDFまたはXLSX、DOCXの形式をご利用ください。';
  }
};

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  class: {
    type: String,
    default: 'px-4 px-md-5 mb-2 row',
  },
  accept: {
    type: String,
    default: '',
  },
  fileName: {
    type: String,
    default: '',
  },
  fileType: {
    type: String,
    default: '',
  },
});

const truncatedFileName = (name: string): string => {
  if (name && name.length > 30) {
    return name.substring(0, 30) + '...';
  } else {
    return name;
  }
};

const removeFile = (file_type: string): void => {
  clearFileError();
  const fileInput = document.getElementById(`file-pdf-${props.id}`) as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
  useOptionalInformation.candidate_daxtra_jsons =
    useOptionalInformation.candidate_daxtra_jsons.filter((item) => item.file_type !== file_type);
  fileName.value = '';
  emit('upload', null);
};

const clearFileError = (): void => {
  messageStore.field_error = messageStore.field_error.filter(
    (error) => error.name !== props.fileType
  );
};
</script>
